import type { RefObject } from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';

import { AutoScroller, Cursor, Feedback, PreventSelection } from '@dnd-kit/dom';
import { RestrictToElement } from '@dnd-kit/dom/modifiers';
import { move } from '@dnd-kit/helpers';
import { DragDropProvider } from '@dnd-kit/react';
import { useSortable } from '@dnd-kit/react/sortable';
import { IconButton, Skeleton } from 'design-system';
import { GripVertical, Move, Settings, X } from 'lucide-react';

import { useAppForm } from '@/features/dashboard/components/AppForm/AppForm/context';
import { ConfirmationDialog } from '@/features/dashboard/components/ConfirmationDialog';
import { dashboardConfirmationPresets } from '@/features/dashboard/components/ConfirmationDialog/presets';
import {
  DrawerStep,
  useDrawerSteps
} from '@/features/dashboard/components/Drawer/context/DrawerStepsContext';
import { convertTilesToActiveSymbols } from '@/features/dashboard/lib/tileSymbolConverter';
import type { TileBigNumber } from '@/features/dashboard/types';
import { AppType } from '@/features/dashboard/types';
import type { TileType } from '@/features/markets';
import { Tile } from '@/features/markets';
import { useAppSelector } from '@/store/hooks';

import {
  selectCurrentTabId,
  selectCurrentTiles
} from '@/features/dashboard/state/dashboardEdit.selectors';
import { useDashboardEditActions } from '../../hooks/useDashboardEditActions';
import MoveTileToDashboardMenu from './MoveTileToDashboardMenu';

type TilesOrderPreview =
  | {
      components?: Array<{
        component?: {
          type?: string;
          tiles?: TileType[];
        };
      }>;
    }
  | null
  | undefined;

type TilesOrderPreviewComponent = NonNullable<
  NonNullable<TilesOrderPreview>['components']
>[number]['component'];

const getTilesFromPreviewComponent = (component: TilesOrderPreviewComponent) => {
  return component?.type === 'tiles' && Array.isArray(component.tiles) ? component.tiles : null;
};

const TileSkeleton = () => (
  <div className='h-14 p-0.5'>
    <Skeleton
      className='h-full'
      data-testid='skeleton'
    />
  </div>
);

const SortableTile = ({
  tile,
  onRequestRemove,
  onRequestEdit,
  onRequestMove,
  index,
  container,
  preview
}: {
  index: number;
  tile: TileBigNumber;
  onRequestRemove: (id: string) => void;
  onRequestEdit: (id: string) => void;
  onRequestMove: (tile: TileBigNumber, anchorEl: HTMLElement) => void;
  container: RefObject<HTMLDivElement | null>;
  preview: TilesOrderPreview;
}) => {
  const handleRef = useRef<HTMLButtonElement | null>(null);
  const { ref } = useSortable({
    id: tile.id,
    index,
    handle: handleRef,
    modifiers: [RestrictToElement.configure({ element: container.current })]
  });

  const content = useMemo((): TileType | undefined => {
    const components = preview?.components ?? [];
    for (const app of components) {
      const previewTiles = getTilesFromPreviewComponent(app.component);
      if (previewTiles) {
        const found = previewTiles.find((t: TileType) => t.id === tile.id);
        if (found) return found;
      }
    }
    return undefined;
  }, [preview, tile.id]);

  return (
    <div
      ref={ref}
      className='relative flex w-[calc(100%+90px)] overflow-hidden border-2 border-dashed border-gx-text-disabled pr-16 pl-6 sm:w-[calc(50%+90px)] lg:w-[calc(25%+90px)]'
    >
      <IconButton
        ref={handleRef}
        className='absolute top-0 left-0 h-full w-6 cursor-grab rounded-none'
        label='Drag to reorder app'
        variant='ghost'
      >
        <GripVertical aria-hidden='true' />
      </IconButton>

      <div className='relative flex-1 overflow-hidden'>
        {content ? (
          <>
            <Tile tile={content} />
            <div className='absolute inset-0 z-10 bg-[#121836]/80' />
          </>
        ) : (
          <TileSkeleton />
        )}
      </div>
      <div className='absolute inset-y-0 right-0 z-20 flex gap-0.5 bg-gx-tile-dark'>
        <IconButton
          className='h-full rounded-none'
          label='Move to ...'
          variant='ghost'
          onClick={e => {
            e.stopPropagation();
            onRequestMove(tile, e.currentTarget);
          }}
        >
          <Move aria-hidden='true' />
        </IconButton>

        <IconButton
          className='h-full rounded-none'
          label='Configuration settings'
          variant='ghost'
          onClick={e => {
            e.stopPropagation();
            onRequestEdit(tile.id);
          }}
        >
          <Settings aria-hidden='true' />
        </IconButton>

        <IconButton
          className='h-full rounded-none'
          label='Remove item'
          variant='ghost'
          onClick={e => {
            e.stopPropagation();
            onRequestRemove(tile.id);
          }}
        >
          <X aria-hidden='true' />
        </IconButton>
      </div>
    </div>
  );
};

export const TilesOrder = ({ preview }: { preview: TilesOrderPreview }) => {
  const currentTabId = useAppSelector(selectCurrentTabId);
  const tiles = useAppSelector(selectCurrentTiles);
  const actions = useDashboardEditActions();

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingRemoveId, setPendingRemoveId] = useState<string | null>(null);

  // Move menu state
  const [moveMenuOpen, setMoveMenuOpen] = useState(false);
  const [moveMenuAnchor, setMoveMenuAnchor] = useState<HTMLElement | null>(null);
  const [tileToMove, setTileToMove] = useState<TileBigNumber | null>(null);

  const { setDrawerStep, setIsEditMode, setEditingTarget } = useDrawerSteps();
  const { setInitialValues } = useAppForm();

  const requestRemove = (id: string) => {
    setPendingRemoveId(id);
    setConfirmOpen(true);
  };

  const requestEdit = useCallback(
    (id: string) => {
      const tile = tiles.find(t => t.id === id);
      if (!tile) return;

      setIsEditMode(true);
      setEditingTarget({ tileId: id });

      // Load existing tiles as active symbols
      const toActive = convertTilesToActiveSymbols(tiles);

      setInitialValues({
        type: AppType.Tiles,
        symbols: { active: toActive, backlog: [] }
      });

      setDrawerStep(DrawerStep.SelectHighlightedPrice);
    },
    [tiles, setEditingTarget, setIsEditMode, setInitialValues, setDrawerStep]
  );

  const requestMove = useCallback((tile: TileBigNumber, anchorEl: HTMLElement) => {
    setTileToMove(tile);
    setMoveMenuAnchor(anchorEl);
    setMoveMenuOpen(true);
  }, []);

  const closeMoveMenu = useCallback(() => {
    setMoveMenuOpen(false);
    setMoveMenuAnchor(null);
    setTileToMove(null);
  }, []);

  const handleRemoveTileFromCurrent = useCallback(() => {
    if (tileToMove && currentTabId) {
      actions.removeTile(currentTabId, tileToMove.id);
    }
    closeMoveMenu();
  }, [tileToMove, currentTabId, actions, closeMoveMenu]);

  const handleConfirmRemove = useCallback(() => {
    if (pendingRemoveId && currentTabId) {
      actions.removeTile(currentTabId, pendingRemoveId);
      setPendingRemoveId(null);
    }
    setConfirmOpen(false);
  }, [pendingRemoveId, currentTabId, actions]);

  const container = useRef<HTMLDivElement | null>(null);
  return (
    <DragDropProvider
      plugins={[Feedback, AutoScroller, Cursor, PreventSelection]}
      onDragEnd={event => {
        const reordered = move(tiles, event);
        actions.updateCurrentTabTiles(reordered);
      }}
    >
      <div
        ref={container}
        className='flex flex-wrap gap-4'
      >
        {tiles.map((tile, index) => (
          <SortableTile
            key={tile.id}
            container={container}
            index={index}
            preview={preview}
            tile={tile}
            onRequestEdit={requestEdit}
            onRequestMove={requestMove}
            onRequestRemove={requestRemove}
          />
        ))}

        <ConfirmationDialog
          open={confirmOpen}
          {...dashboardConfirmationPresets.removeDashboardItem}
          onCancel={() => {
            setConfirmOpen(false);
          }}
          onConfirm={handleConfirmRemove}
        />

        {tileToMove ? (
          <MoveTileToDashboardMenu
            anchorEl={moveMenuAnchor}
            open={moveMenuOpen}
            tile={tileToMove}
            onClose={closeMoveMenu}
            onRemoveFromCurrent={handleRemoveTileFromCurrent}
          />
        ) : null}
      </div>
    </DragDropProvider>
  );
};
